import React, { useState } from "react";
import { FieldValues, useController } from "react-hook-form";
import { Skeleton } from "antd5";

import Spinner from "components/ui/spinner/Spinner";
import { useDebouncedValue } from "lib/debounce";
import { useOrganisations } from "lib/hooks/api/organisations/useOrganisations";
import { useSignalSettingCountryCodes } from "lib/hooks/api/teams/useSignalSettings";
import {
  ExportOrgSortBy as BuyerSearchSortBy,
  ExportOrgSortOrder as BuyerSearchSortOrder,
  SearchOrgRole,
} from "lib/types/graphQLEnums";
import { Select, SelectProps } from "./Inputs";

type BuyerSelectProps<T extends FieldValues> = Omit<SelectProps<T>, "options" | "selectAll"> & {
  selectAll?: boolean;
};

export function BuyerSelect<T extends FieldValues>({
  selectAll = true,
  ...props
}: BuyerSelectProps<T>) {
  const { field } = useController(props);
  const [searchText, setSearchText] = useState<string>("");
  const [debouncedText] = useDebouncedValue(searchText, 300);
  const selectedBuyerIds = Array.isArray(field.value)
    ? field.value
    : field.value != null
      ? [field.value]
      : [];

  const userCountryCodes = useSignalSettingCountryCodes();

  // used to fetch the names of already selected buyers (when we just have the ids)
  const fetchSelectedBuyers = selectedBuyerIds.length > 0 && !debouncedText;

  const { data: selectedBuyers, isLoading: isLoadingSelected } = useOrganisations(
    {
      limit: selectedBuyerIds.length,
      page: 1,
      // No countryCodes: an already-selected buyer must resolve its name even when it sits
      // outside the user's feed countries.
      ids: selectedBuyerIds,
      role: SearchOrgRole.Buyer,
      sortBy: BuyerSearchSortBy.NoticeCount,
      sortOrder: BuyerSearchSortOrder.Desc,
    },
    {
      enabled: fetchSelectedBuyers,
      staleTime: Infinity,
      cacheTime: Infinity,
    },
  );

  const { data: dropdownBuyers, isLoading } = useOrganisations(
    {
      sortBy: BuyerSearchSortBy.NoticeCount,
      sortOrder: BuyerSearchSortOrder.Desc,
      role: SearchOrgRole.Buyer,
      limit: 20, // Hard coded limit which means users will only see the top 20 buyer hits in the filter dropdown. As these 20 buyers are sorted by notice count, this is limit is likely enough.
      page: 1,
      names: debouncedText ? [debouncedText] : undefined,
      countryCodes: userCountryCodes,
    },
    { enabled: !fetchSelectedBuyers && debouncedText.length > 1 },
  );

  let options: {
    label: string;
    value: string | number;
  }[] = dropdownBuyers?.searchOrganisations.orgs
    ? dropdownBuyers?.searchOrganisations.orgs.map((buyer) => ({
        label: buyer.name,
        value: buyer.id,
      }))
    : [];

  // we're only going to show the initial buyers if we have no search text and no options
  if (!debouncedText && selectedBuyers?.searchOrganisations.orgs && options.length === 0) {
    options = selectedBuyers?.searchOrganisations.orgs.map((buyer) => ({
      label: buyer.name,
      value: buyer.id,
    }));
  }

  const loading =
    (isLoading && !fetchSelectedBuyers && !dropdownBuyers) ||
    (isLoadingSelected && fetchSelectedBuyers && !selectedBuyers);

  const renderDropdown = (menu: React.ReactElement) => {
    if (loading && !!debouncedText) {
      return <Skeleton style={{ padding: "8px", paddingBottom: "0px" }} active />;
    }

    return menu;
  };

  const dropdownStyle = debouncedText === "" ? { display: "none" } : {};

  return (
    <Select
      {...props}
      selectAll={selectAll && !!debouncedText}
      loading={loading && !!debouncedText}
      options={options}
      optionFilterProp="label"
      labelRender={({ label, value }) =>
        label == null || label.toString() === value?.toString() ? (
          <Spinner color="currentColor" label="Loading buyer" />
        ) : (
          label
        )
      }
      dropdownRender={renderDropdown}
      dropdownStyle={dropdownStyle}
      onSearch={(val) => setSearchText(val)}
      showSearch
      filterOption={false}
    />
  );
}
